import { useState, useEffect } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import { useMedplum } from '@medplum/react'
import { config } from '@/config'
import { logError } from './errorUtils'

interface GeocodedCoords {
  lat: number
  lon: number
}

/**
 * Why the lookup produced no coordinates: `not-found` = the service answered but knows
 * no such place (a bad ZIP is the patient's problem), `unavailable` = the service could
 * not be asked or errored (nothing to do with the address). Callers word their notices
 * off this distinction.
 */
export type GeocodeFailure = 'not-found' | 'unavailable'

async function geocodeAddress(
  query: string,
  token?: string,
): Promise<GeocodedCoords | GeocodeFailure> {
  const baseUrl = config.middlewareUrl || ''
  if (!baseUrl) return 'unavailable'
  try {
    const resp = await fetch(`${baseUrl}/order/geocode?q=${encodeURIComponent(query)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!resp.ok) return 'unavailable'
    const text = await resp.text()
    if (text) {
      const data = JSON.parse(text) as GeocodedCoords | null
      if (data && data.lat != null && data.lon != null) return data
    }
    return 'not-found'
  } catch (err) {
    logError('Patient geocode unreachable', err)
    return 'unavailable'
  }
}

const isCoords = (r: GeocodedCoords | GeocodeFailure): r is GeocodedCoords => typeof r === 'object'

/**
 * Geocodes the patient's primary address and returns coordinates + state.
 * Falls back gracefully if geocoding is unavailable.
 */
export function usePatientGeocode(patient: Patient, options?: { zipOnly?: boolean }) {
  const medplum = useMedplum()
  const [coords, setCoords] = useState<GeocodedCoords | null>(null)
  const [loading, setLoading] = useState(false)
  const [fallbackMode, setFallbackMode] = useState(false)
  const [fallbackReason, setFallbackReason] = useState<GeocodeFailure | null>(null)

  const activeAddress = patient.address?.find((a) => a.use !== 'old')
  const patientState = activeAddress?.state?.toUpperCase().trim() || null
  const patientPostalCode = activeAddress?.postalCode

  useEffect(() => {
    // A new patient starts clean — the previous patient's outcome must not leak, and
    // that includes a patient with no address at all, who must not inherit the last
    // patient's coordinates (the order form stays mounted across patient switches).
    setCoords(null)
    setFallbackMode(false)
    setFallbackReason(null)
    const addr = patient.address?.find((a) => a.use !== 'old')
    if (!addr) return

    const run = async () => {
      setLoading(true)
      try {
        const fullQuery = [addr.line?.join(' '), addr.city, addr.state, addr.postalCode]
          .filter(Boolean)
          .join(', ')
        const token = medplum.getAccessToken()

        let result: GeocodedCoords | GeocodeFailure = options?.zipOnly
          ? 'not-found'
          : await geocodeAddress(fullQuery, token)
        if (!isCoords(result) && addr.postalCode) {
          result = await geocodeAddress(addr.postalCode, token)
        }

        if (isCoords(result)) {
          setCoords(result)
        } else {
          setFallbackMode(true)
          setFallbackReason(result)
        }
      } catch {
        setFallbackMode(true)
        setFallbackReason('unavailable')
      } finally {
        setLoading(false)
      }
    }

    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.id, patientPostalCode, medplum, options?.zipOnly])

  return { coords, patientState, loading, fallbackMode, fallbackReason }
}
